<template>
  <div class="w-full p-0 lg:p-8 flex flex-col justify-center">
    <template v-if="isLoading">
      <span class="w-full h-12 flex items-center justify-center">
        <lottie :options="LOADING_ANIMATION_OPTIONS_DARK" />
      </span>
    </template>

    <template v-else-if="submitSuccess">
      <div class="flex flex-col items-center justify-center h-full">
        <img src="/img/connections/icons/success.png" alt="Checkmark" />
        <h3 class="text-center m-4">Success!</h3>
        <p class="lg:w-1/2 text-center mb-6">
          Your sign up enquiry has been sent.
        </p>
        <p class="text-center text-truegray-500">
          Check your emails for a reply soon!
        </p>
      </div>
    </template>

    <template v-else>
      <div class="mb-8">
        <h1 class="text-cw-900">Join CareerWise Connections</h1>
        <p>
          Search for your school below. If you can't find the school you're
          looking for, or they do not yet support Connections, please submit
          your contact details and school name. We'll reach out to invite the
          school and be in touch soon!
        </p>
      </div>

      <FormulateForm v-model="registerForm" @submit="onSubmit">
        <div class="flex flex-col gap-4">
          <!-- Show school selection -->
          <template v-if="schoolExists">
            <!-- School selection -->
            <FormulateInput
              type="autocomplete"
              name="school"
              label="Search for your school"
              :options="schoolsList"
              validation="bail|required|valid"
              :validation-rules="{
                valid: ({ value }) =>
                  schoolsList.find((school) => school.label === value),
              }"
              :validation-messages="{
                valid: 'Please select a valid school.',
              }"
              error-behavior="submit"
            />
            <!-- /School selection -->

            <h6 class="text-truegray-500">
              Can't find the school you're looking for?
              <a
                class="text-sm font-semibold underline hover:text-cw-alt ml-2"
                @click="setSchoolExists(false)"
              >
                Invite them!
              </a>
            </h6>
          </template>
          <!-- /Show school selection -->

          <!-- Show school invitation -->
          <template v-if="!schoolExists">
            <div class="flex flex-col gap-3">
              <!-- Name -->
              <FormulateInput
                name="name"
                label="Your full name"
                validation="required:trim"
                validation-name="Full name"
              />
              <!-- /Name -->

              <!-- Email -->
              <FormulateInput
                name="email"
                label="Your email"
                validation="bail|required:trim|email"
                validation-name="Email"
              />
              <!-- /Email -->

              <!-- Phone number -->
              <FormulateInput
                name="phone"
                label="Your phone number"
                validation="bail|required:trim|number|valid"
                validation-name="Phone number"
                :validation-rules="{
                  valid: ({ value }) => /^[0-9]+$/.test(value),
                }"
                :validation-messages="{
                  valid: 'Please enter a valid phone number.',
                }"
              />
              <!-- /Phone number -->

              <!-- School name -->
              <FormulateInput
                name="school"
                label="School name"
                validation="bail|required:trim"
                validation-name="School name"
              />
              <!-- /School name -->

              <!-- Comments -->
              <FormulateInput
                name="comments"
                label="Extra comments (optional)"
                type="textarea"
                rows="4"
              />
              <!-- /Comments -->
            </div>
            <h6 class="text-truegray-500">
              <a
                class="text-sm font-semibold underline hover:text-cw-alt"
                @click="setSchoolExists(true)"
              >
                Search for a school instead.
              </a>
            </h6>
          </template>
          <!-- /Show school invitation -->

          <!-- Submit button -->
          <div class="mt-6">
            <FormulateInput
              type="submit"
              :disabled="isSubmitting"
              :input-class="['w-52 btn-cw-alt mb-0']"
              :element-class="['flex justify-center']"
            >
              <span
                v-if="isSubmitting"
                class="w-full h-6 flex items-center justify-center"
              >
                <lottie :options="LOADING_ANIMATION_OPTIONS" />
              </span>

              <span v-else class="flex flex-none items-center">
                {{ submitButtonText }}
                <span v-if="schoolExists">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    class="w-5 h-5 ml-2"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M8.22 5.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L11.94 10 8.22 6.28a.75.75 0 0 1 0-1.06Z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </span>
              </span>
            </FormulateInput>
          </div>
          <!-- /Submit button -->
        </div>
      </FormulateForm>
    </template>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted } from 'vue'
import { useStore } from '../../../js/store/index.js'
import * as loadingAnimation from '../../../shared/components/loading.json'
import * as loadingAnimationDark from '../../../shared/components/loading-dark.json'

export default defineComponent({
  name: 'ModalRegisterOrganisation',

  setup() {
    const store = useStore()

    const isLoading = ref(false)
    const LOADING_ANIMATION_OPTIONS = {
      animationData: loadingAnimation.default,
    }
    const LOADING_ANIMATION_OPTIONS_DARK = {
      animationData: loadingAnimationDark.default,
    }

    const schoolExists = ref(true)
    const setSchoolExists = (val) => {
      schoolExists.value = val
    }

    const registerForm = ref({
      school: null,
      name: null,
      email: null,
      phone: null,
      comments: null,
    })

    const isSubmitting = ref(false)
    const submitSuccess = ref(false)
    const submitButtonText = computed(() => {
      return schoolExists.value ? 'Sign up with school' : 'Submit'
    })
    const schoolsList = computed(
      () => store.getters['careerwise/getSchoolsList'],
    )

    const selectedSchool = computed(() => {
      const school = registerForm.value.school
      return school ? schoolsList.value.find((s) => s.label === school) : null
    })

    const onSubmit = async () => {
      if (schoolExists.value) {
        // Route to school domain
        const domain = selectedSchool.value.domain
        const url = `https://${domain}.careerwise.school/add-connection`
        window.open(url, '_blank')
      } else {
        const payload = {
          name: registerForm.value.name,
          email: registerForm.value.email,
          phone: registerForm.value.phone,
          school: registerForm.value.school,
          comments: registerForm.value.comments,
        }
        isSubmitting.value = true
        const responseSuccess = await store.dispatch(
          'careerwise/dispatchSendConnectionParentRegistration',
          payload,
        )
        isSubmitting.value = false
        submitSuccess.value = responseSuccess
      }
    }

    const initForm = async () => {
      isLoading.value = true
      registerForm.value.name = null
      registerForm.value.school = null
      registerForm.value.email = null
      registerForm.value.phone = null
      registerForm.value.comments = null

      await store.dispatch('careerwise/dispatchGetSchools')
      isLoading.value = false
    }

    onMounted(async () => await initForm())

    return {
      isLoading,
      LOADING_ANIMATION_OPTIONS,
      LOADING_ANIMATION_OPTIONS_DARK,

      schoolsList,
      schoolExists,
      setSchoolExists,

      registerForm,
      isSubmitting,
      submitSuccess,
      submitButtonText,
      onSubmit,
    }
  },
})
</script>
